import { motion } from 'motion/react';
import { Sparkles, PenTool, ArrowLeft } from 'lucide-react';

export type GenerateMode = 'ai' | 'manual';

interface GenerateModeSelectorProps {
  onSelectMode: (mode: GenerateMode) => void;
  onCancel: () => void;
}

export function GenerateModeSelector({ onSelectMode, onCancel }: GenerateModeSelectorProps) {
  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-6">
      <div className="w-full max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h2 className="text-4xl mb-3 text-neutral-900">
            How would you like to create your workout?
          </h2>
          <p className="text-neutral-600 mb-12">
            Choose AI-powered generation or build it yourself.
          </p>

          <div className="space-y-4">
            {/* AI Mode Option */}
            <motion.button
              onClick={() => onSelectMode('ai')}
              className="w-full text-left bg-neutral-900 text-white rounded-xl p-6 hover:bg-neutral-800 transition-all"
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-2">AI-Powered</h3>
                  <p className="text-white/70">
                    Answer a few questions and let AI create a personalized workout tailored to your needs, location, and physical considerations.
                  </p>
                  <div className="flex items-center gap-3 mt-3 text-sm text-white/50">
                    <span>5 quick steps</span>
                    <span>•</span>
                    <span>Personalized</span>
                    <span>•</span>
                    <span>Smart recommendations</span>
                  </div>
                </div>
              </div>
            </motion.button>

            {/* Manual Mode Option */}
            <motion.button
              onClick={() => onSelectMode('manual')}
              className="w-full text-left bg-white border border-neutral-200 rounded-xl p-6 hover:border-neutral-400 hover:bg-neutral-50 transition-all"
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center flex-shrink-0">
                  <PenTool className="w-6 h-6 text-neutral-700" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-2 text-neutral-900">Manual Builder</h3>
                  <p className="text-neutral-600">
                    Build your workout from scratch by selecting exercises from our library. Full control over every detail.
                  </p>
                  <div className="flex items-center gap-3 mt-3 text-sm text-neutral-500">
                    <span>Full control</span>
                    <span>•</span>
                    <span>Search exercises</span>
                    <span>•</span>
                    <span>Custom structure</span>
                  </div>
                </div>
              </div>
            </motion.button>
          </div>
        </motion.div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-12">
          <button
            onClick={onCancel}
            className="flex items-center gap-2 text-neutral-500 hover:text-neutral-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
